import { AxiosRequestConfig } from 'axios';
import { mapToData } from 'src/common/http/mapToData';
import { mapToErrors } from 'src/common/http/mapToErrors';

import instance from '../../../common/http/axiosInstance';
import {
  AffiliateDetail,
  AffiliateListParams,
  AffiliateListResponse,
  AffiliateTaxInfoDetail,
  CreateAffiliateDto,
  type PayoutTermsByAudienceResponse,
  type PayoutTermsByTierResponse,
  RevokeAffiliateTaxInfoDto,
  type SetTierProtectionDto,
  UpdateAffiliateDto,
} from '../domain/types';

const requests = {
  get: async <T>(url: string, config?: AxiosRequestConfig): Promise<T> =>
    instance
      .get(url, config)
      .then(mapToData)
      .catch((err) => {
        if (err.response?.status === 404) {
          return null;
        }

        return mapToErrors(err);
      }),
  post: async <T, K>(url: string, body?: T, config?: AxiosRequestConfig<T>): Promise<K> =>
    instance.post(url, body, config).then(mapToData).catch(mapToErrors),
  patch: async <T, K>(url: string, body?: T, config?: AxiosRequestConfig<T>): Promise<K> =>
    instance.patch(url, body, config).then(mapToData).catch(mapToErrors),
  put: async <T>(url: string, body?: T, config?: AxiosRequestConfig<T>): Promise<void> =>
    instance
      .put(url, body, config)
      .then(() => undefined)
      .catch(mapToErrors),
  delete: async (url: string): Promise<void> =>
    instance
      .delete(url)
      .then(() => undefined)
      .catch(mapToErrors),
};

export const affiliateManagementService = {
  list: async (projectId: string, params?: AffiliateListParams): Promise<AffiliateListResponse> =>
    requests.get<AffiliateListResponse>(`projects/${projectId}/affiliates`, { params }),

  getDetail: async (projectId: string, affiliateId: string): Promise<AffiliateDetail> =>
    requests.get<AffiliateDetail>(`projects/${projectId}/affiliates/${affiliateId}`),

  create: async (projectId: string, body: CreateAffiliateDto): Promise<AffiliateDetail> =>
    requests.post<CreateAffiliateDto, AffiliateDetail>(`projects/${projectId}/affiliates`, body),

  update: async (projectId: string, affiliateId: string, body: UpdateAffiliateDto): Promise<AffiliateDetail> =>
    requests.patch<UpdateAffiliateDto, AffiliateDetail>(`projects/${projectId}/affiliates/${affiliateId}`, body),

  exportCsv: async (projectId: string, params?: AffiliateListParams): Promise<void> =>
    requests.post<AffiliateListParams, void>(`projects/${projectId}/affiliates/export`, params),

  suspend: async (projectId: string, affiliateId: string): Promise<void> =>
    requests.patch<UpdateAffiliateDto, void>(`projects/${projectId}/affiliates/${affiliateId}`, { status: 'Paused' }),

  reactivate: async (projectId: string, affiliateId: string): Promise<void> =>
    requests.patch<UpdateAffiliateDto, void>(`projects/${projectId}/affiliates/${affiliateId}`, { status: 'Active' }),

  getPayoutTermsByAudience: async (projectId: string, audienceIds: string[]): Promise<PayoutTermsByAudienceResponse> =>
    requests.get<PayoutTermsByAudienceResponse>(`projects/${projectId}/payout-terms/by-audience`, {
      params: { audience_ids: audienceIds.length > 0 ? audienceIds.join(',') : 'null' },
    }),

  getPayoutTermsByTier: async (projectId: string, tierIds: string[]): Promise<PayoutTermsByTierResponse> =>
    requests.get<PayoutTermsByTierResponse>(`projects/${projectId}/payout-terms/by-tier`, {
      params: { tier_ids: tierIds.length > 0 ? tierIds.join(',') : 'null' },
    }),

  /**
   * Admin-only view of an affiliate's tax info. Sensitive fields (tax_id_number, foreign_tax_id)
   * are returned as `*_masked` values only. Returns `null` when the affiliate hasn't submitted tax info.
   */
  getAffiliateTaxInfo: async (projectId: string, userIdentifier: string): Promise<AffiliateTaxInfoDetail | null> =>
    requests.get<AffiliateTaxInfoDetail | null>(
      `projects/${projectId}/affiliates/${encodeURIComponent(userIdentifier)}/tax-status`,
    ),

  revokeAffiliateTaxInfo: async (
    projectId: string,
    userIdentifier: string,
    body: RevokeAffiliateTaxInfoDto,
  ): Promise<AffiliateTaxInfoDetail> =>
    requests.post<RevokeAffiliateTaxInfoDto, AffiliateTaxInfoDetail>(
      `projects/${projectId}/affiliates/${encodeURIComponent(userIdentifier)}/tax-info/revoke`,
      body,
    ),

  setTierProtection: async (projectId: string, affiliateId: string, body: SetTierProtectionDto): Promise<void> =>
    requests.put<SetTierProtectionDto>(`projects/${projectId}/affiliates/${affiliateId}/tier-protection`, body),

  clearTierProtection: async (projectId: string, affiliateId: string): Promise<void> =>
    requests.delete(`projects/${projectId}/affiliates/${affiliateId}/tier-protection`),
};
